import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { LoadingOverlayComponent } from '../../../shared/loading-overlay.component';
import { ProgramacionMasivaFacade } from '../services/programacion-masiva.facade';
import {
  PmFiltrosComponent,
  PmFiltrosForm
} from '../components/pm-filtros.component';
import { PmAsesoresTableComponent } from '../components/pm-asesores-table.component';
import { PmBuscarDialogComponent } from '../components/pm-buscar-dialog.component';
import { PmErroresDialogComponent } from '../components/pm-errores-dialog.component';
import { ProgramacionMasivaDetalle } from 'src/app/domain/model/agenda/programacion-masiva';
// Diálogos para campos condicionales (Legacy: tableComplementarios, tipos 42 y 43)
import { BusquedaClienteDialogComponent } from '../../../common/busquedas/busqueda-cliente-dialog.component';
import { BusquedaCentroTrabajoDialogComponent } from '../../../common/busquedas/busqueda-centro-trabajo-dialog.component';
import { BusquedaTareaDialogComponent } from '../../../common/busquedas/busqueda-tarea-dialog.component';

/**
 * Smart Component: Programación Masiva de Citas
 *
 * Solo orquesta los Dumb Components y delega al Facade.
 * NO tiene lógica de negocio directa.
 *
 * Migrado desde: ProgramacionMasivaViewImpl.java
 */
@Component({
  selector: 'app-programacion-masiva',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    ToastModule,
    TooltipModule,
    LoadingOverlayComponent,
    PmFiltrosComponent,
    PmAsesoresTableComponent,
    PmBuscarDialogComponent,
    PmErroresDialogComponent,
    // Diálogos para campos condicionales (Legacy: tableComplementarios)
    BusquedaClienteDialogComponent,
    BusquedaCentroTrabajoDialogComponent,
    BusquedaTareaDialogComponent
  ],
  providers: [MessageService, ProgramacionMasivaFacade],
  template: `
    <div class="px-3 py-4 sm:px-4 lg:px-6 max-w-6xl mx-auto">
      <p-toast></p-toast>
      <app-loading-overlay [blocked]="facade.loading()" />

      <!-- Breadcrumb -->
      <div
        class="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-2 flex flex-wrap items-center gap-2"
      >
        <span>Administrar agenda - Selección de consultor</span>
        <span class="text-gray-400">»</span>
        <span class="font-medium text-gray-800 dark:text-gray-200"
          >Programación Masiva Citas</span
        >
      </div>

      <!-- Header con botones (Legacy: Limpiar | Nueva | Programar Cita | No Programados | Regresar) -->
      <div class="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <h1 class="text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-100">
          Consultar de Consultores
        </h1>
        <div class="flex flex-wrap items-stretch gap-2 sm:gap-3 lg:justify-end">
          <!-- Legacy: saRegresar - siempre visible -->
          <p-button
            label="Regresar"
            icon="pi pi-arrow-left"
            [outlined]="true"
            styleClass="w-full sm:w-auto min-h-[44px]"
            (onClick)="facade.regresar()"
          ></p-button>

          <!-- Legacy: siLimpiar - siempre visible, limpia el formulario -->
          <p-button
            label="Limpiar"
            icon="pi pi-eraser"
            severity="secondary"
            [outlined]="true"
            styleClass="w-full sm:w-auto min-h-[44px]"
            (onClick)="facade.limpiar()"
            pTooltip="Limpiar formulario"
          ></p-button>

          <!-- Legacy: saNuevaProgramacion - visible después de ejecutar (éxito o con errores) -->
          @if (facade.ejecucionCompletada()) {
            <p-button
              label="Nueva"
              icon="pi pi-plus"
              styleClass="w-full sm:w-auto min-h-[44px]"
              (onClick)="facade.reset()"
              pTooltip="Nueva Programación"
            ></p-button>
          }

          <!-- Legacy: saDetalleError - visible después de programar (cuando hay errores) -->
          @if (facade.asesoresResultado().length > 0) {
            <p-button
              label="No Programados"
              icon="pi pi-list"
              severity="warn"
              [outlined]="true"
              styleClass="w-full sm:w-auto min-h-[44px]"
              (onClick)="facade.mostrarDialogErrores.set(true)"
              pTooltip="Ver detalle de errores"
            ></p-button>
          }

          <!-- Legacy: saProgramarCita - visible cuando hay asesores pendientes (sin cita) -->
          @if (facade.hayAsesoresPendientes()) {
            <p-button
              label="Programar Cita"
              icon="pi pi-check"
              severity="success"
              styleClass="w-full sm:w-auto min-h-[44px]"
              [loading]="facade.loading()"
              (onClick)="facade.programarCitas()"
            ></p-button>
          }
        </div>
      </div>

      <!-- Filtros (incluye campos condicionales para tipos 42 y 43) -->
      <!-- Legacy: activarCampos(false) bloquea fecha/hora/duración/observaciones si hay programados -->
      <app-pm-filtros
        [form]="facade.formState()"
        [tiposCita]="facade.tiposCita()"
        [oficinas]="facade.oficinas()"
        [tiposAsesor]="facade.tiposAsesor()"
        [cantidadAsesores]="facade.asesoresSeleccionados().length"
        [readonly]="false"
        [readonlyFechaHora]="facade.hayAsesoresProgramados()"
        (formChange)="onFormChange($event)"
        (buscarAsesores)="facade.abrirDialogoAsesores()"
        (limpiarConsultores)="onLimpiarConsultores()"
        (buscarBeneficiario)="facade.abrirDialogoBeneficiario()"
        (buscarCentroTrabajo)="facade.abrirDialogoCentroTrabajo()"
        (buscarTarea)="facade.abrirDialogoTarea()"
      >
      </app-pm-filtros>

      <!-- Tabla de Asesores (Legacy: tabla siempre editable, puede eliminar y reintentar) -->
      <app-pm-asesores-table
        [asesores]="facade.asesoresSeleccionados()"
        [readonly]="false"
        (eliminar)="facade.eliminarAsesor($event)"
      >
      </app-pm-asesores-table>

      <!-- Dialog: Buscar Asesores -->
      <app-pm-buscar-dialog
        [visible]="facade.mostrarDialogAsesores()"
        [asesores]="facade.asesoresDisponibles()"
        (visibleChange)="onDialogAsesoresChange($event)"
        (agregar)="facade.agregarAsesores($event)"
      >
      </app-pm-buscar-dialog>

      <!-- Dialog: Resultados (Legacy: ListadoErroresProgramacionMasiva) -->
      <app-pm-errores-dialog
        [visible]="facade.mostrarDialogErrores()"
        [asesores]="facade.asesoresResultado()"
        (visibleChange)="onDialogErroresChange($event)"
      >
      </app-pm-errores-dialog>

      <!-- ==================== DIALOGS CAMPOS CONDICIONALES ==================== -->
      @if (facade.mostrarDialogBeneficiario()) {
        <app-busqueda-cliente-dialog
          (onSelect)="facade.seleccionarBeneficiario($event)"
          (onClose)="facade.cerrarDialogoBeneficiario()"
        >
        </app-busqueda-cliente-dialog>
      }

      @if (facade.mostrarDialogCentroTrabajo()) {
        <app-busqueda-centro-trabajo-dialog
          [nmconsecutivoBeneficiario]="facade.formState().beneficiarioId"
          (onSelect)="facade.seleccionarCentroTrabajo($event)"
          (onClose)="facade.cerrarDialogoCentroTrabajo()"
        >
        </app-busqueda-centro-trabajo-dialog>
      }

      @if (facade.mostrarDialogTarea()) {
        <app-busqueda-tarea-dialog
          (onSelect)="facade.seleccionarTarea($event)"
          (onClose)="facade.cerrarDialogoTarea()"
        >
        </app-busqueda-tarea-dialog>
      }
    </div>
  `
})
export class ProgramacionMasivaComponent implements OnInit {
  readonly facade = inject(ProgramacionMasivaFacade);

  async ngOnInit(): Promise<void> {
    await this.facade.cargarCatalogos();
  }

  onFormChange(cambios: Partial<PmFiltrosForm>): void {
    Object.entries(cambios).forEach(([campo, valor]) => {
      this.facade.updateForm(campo as keyof PmFiltrosForm, valor);
    });

    // Si cambió el tipo de cita, verificar si hay que limpiar campos condicionales
    if ('tipoCita' in cambios && !this.facade.mostrarCamposComplementarios()) {
      this.facade.limpiarCamposComplementarios();
    }
  }

  onDialogAsesoresChange(visible: boolean): void {
    if (!visible) {
      this.facade.cerrarDialogoAsesores();
    }
  }

  onDialogErroresChange(visible: boolean): void {
    if (!visible) {
      this.facade.cerrarDialogoErrores();
    }
  }

  onLimpiarConsultores(): void {
    this.facade.limpiarConsultores();
  }
}
